import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature dispatch-details
import * as H from '../helpers';

const selectOrderDetailsStore = (state) => state.orderDetails;

const makeSelectEvents = () => createSelector(
  selectOrderDetailsStore,
  ({ events }) => R.sortBy(R.prop(GC.FIELD_CLO_EVENT_INDEX), R.values(events)),
);

const makeSelectOrderDetails = () => createSelector(
  selectOrderDetailsStore,
  ({ details }) => details,
);

const makeSelectBillToVersion = () => createSelector(
  selectOrderDetailsStore,
  R.path(['details', GC.SYSTEM_OBJECT_BILL_TO, GC.FIELD_VERSION]),
);

const makeSelectFirstList = () => createSelector(
  selectOrderDetailsStore,
  ({ lists, firstActiveListName }) => R.pathOr([], [firstActiveListName], lists),
);

const makeSelectSecondList = () => createSelector(
  selectOrderDetailsStore,
  ({ lists, secondActiveListName }) => R.pathOr([], [secondActiveListName], lists),
);

const makeSelectLists = () => createSelector(
  selectOrderDetailsStore,
  ({ lists }) => lists,
);

const makeSelectOrderGuid = () => createSelector(
  selectOrderDetailsStore,
  ({ details }) => R.path([GC.FIELD_GUID], details),
);

const makeSelectLoadConfigs = () => createSelector(
  selectOrderDetailsStore,
  ({ loadConfigs }) => loadConfigs,
);

const makeSelectCharges = () => createSelector(
  selectOrderDetailsStore,
  ({ charges }) => charges,
);

const makeSelectSelectedCustomerRate = () => createSelector(
  selectOrderDetailsStore,
  R.compose(
    R.find(R.pathEq(true, ['selected'])),
    R.pathOr([], ['lists', 'customerRateList']),
  ),
);

const makeSelectActiveListNames = () => createSelector(
  selectOrderDetailsStore,
  ({ activeLists }) => activeLists,
);

const makeSelectActiveLists = () => createSelector(
  selectOrderDetailsStore,
  ({ lists, activeLists }) => R.compose(
    R.indexBy(R.prop('listName')),
    R.map((listName) => ({
      listName,
      pagination: R.path([listName, 'pagination'], lists),
      totalCount: R.path([listName, 'totalCount'], lists),
      list: R.or(R.path([listName, 'list'], lists), R.path([listName], lists)),
    })),
    R.uniq,
  )(activeLists),
);

const makeSelectOrderTotalWeight = () => createSelector(
  selectOrderDetailsStore,
  ({ details }) => {
    const items = R.pathOr([], [GC.FIELD_LOAD_ITEMS], details);
    if (G.isNotNilAndNotEmpty(items)) {
      const total = G.calcItemsTotalWeightWithoutQty(items);

      return G.renameKeys({
        [GC.FIELD_ITEM_WEIGHT]: GC.FIELD_TOTAL_TRIP_WEIGHT,
        [GC.FIELD_ITEM_WEIGHT_TYPE]: GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
      }, total);
    }

    return {};
  },
);

const makeSelectOrderTotalDistance = () => createSelector(
  selectOrderDetailsStore,
  ({ details }) => {
    const distances = R.compose(
      R.filter(G.isNotNilAndNotEmpty),
      R.map(R.prop(GC.FIELD_DISTANCE_CLO)),
      R.pathOr([], [GC.FIELD_LOAD_STOPS]),
    )(details);

    return G.calculateTotalDistance(distances);
  },
);

const makeSelectOrderItemsVolume = () => createSelector(
  selectOrderDetailsStore,
  ({ details }) => G.calculateTotalVolumeWithoutQty(R.pathOr([], ['items'], details)),
);

const makeSelectLoadDocuments = () => createSelector(
  selectOrderDetailsStore,
  ({ documents }) => documents,
);

const makeSelectStatusMessageConfigs = () => createSelector(
  selectOrderDetailsStore,
  ({ loadConfigs: { statusMessageConfigs } }) => statusMessageConfigs,
);

const makeSelectMessageCenter = () => createSelector(
  selectOrderDetailsStore,
  ({ messageCenter }) => messageCenter,
);

const makeSelectMessageCenterList = () => createSelector(
  selectOrderDetailsStore,
  ({ messageCenter }) => {
    const { activeTab } = messageCenter;
    const entities = R.pathOr([], [activeTab], messageCenter);

    return { entities, activeTab };
  },
);

const makeSelectNotes = () => createSelector(
  selectOrderDetailsStore,
  R.pathOr([], ['messageCenter', 'noteList']),
);

const makeSelectRouteTelConfigs = () => createSelector(
  selectOrderDetailsStore,
  ({ loadConfigs }) => G.getRouteTelConfigsForCreateClo(R.prop('configsByNames', loadConfigs)),
);

const makeSelectMailList = () => createSelector(
  selectOrderDetailsStore,
  ({ mailList }) => mailList,
);

const makeSelectRelatedLoadList = () => createSelector(
  selectOrderDetailsStore,
  ({ relatedLoadList }) => relatedLoadList,
);

const makeSelectRelatedLoadListLoading = () => createSelector(
  selectOrderDetailsStore,
  ({ relatedLoadListLoading }) => relatedLoadListLoading,
);

const makeSelectLinkedLoadList = () => createSelector(
  selectOrderDetailsStore,
  ({ linkedLoadList }) => linkedLoadList,
);

const makeSelectPinnedNote = () => createSelector(
  selectOrderDetailsStore,
  ({ messageCenter }) => R.compose(
    R.find(R.propEq(true, 'pinned')),
    R.prop('noteList'),
  )(messageCenter),
);

const makeSelectNotification = () => createSelector(
  selectOrderDetailsStore,
  ({ notification }) => notification,
);

const makeSelectCustomerInvoiceList = () => createSelector(
  selectOrderDetailsStore,
  ({ lists }) => lists.customerInvoiceList,
);

const makeSelectCustomerInvoiceNumberList = () => createSelector(
  R.pathOr([], ['orderDetails', 'lists', 'customerInvoiceList']),
  R.map(R.pick([GC.FIELD_GUID, GC.FIELD_INVOICE_NUMBER])),
);

const makeSelectExpandedLists = () => createSelector(
  selectOrderDetailsStore,
  ({ expandedLists }) => expandedLists,
);

const makeSelectHiddenTabs = () => createSelector(
  selectOrderDetailsStore,
  ({ hiddenTabs }) => hiddenTabs,
);

const makeSelectInvoiceTotals = () => createSelector(
  selectOrderDetailsStore,
  ({ invoiceTotals }) => invoiceTotals,
);

const makeSelectLoadCustomStatuses = () => createSelector(
  selectOrderDetailsStore,
  ({ loadCustomStatuses }) => loadCustomStatuses,
);

const makeSelectOrderTransportationModeGroupingList = () => createSelector(
  selectOrderDetailsStore,
  ({ transportationModeGroupingList }) => transportationModeGroupingList,
);

const makeSelectClaimGeneralDetails = () => createSelector(
  selectOrderDetailsStore,
  ({ claimGeneralDetails }) => claimGeneralDetails,
);

const makeSelectCubicCapacityWarning = createSelector(
  [
    R.pathOr([], ['orderDetails', 'details', GC.FIELD_LOAD_ITEMS]),
    R.path(['orderDetails', 'loadConfigs', 'configGroups', GC.GENERAL_CONFIG_GROUP]),
  ],
  H.getCubicCapacityWarningErrors,
);

export {
  makeSelectLists,
  makeSelectNotes,
  makeSelectEvents,
  makeSelectCharges,
  makeSelectMailList,
  makeSelectOrderGuid,
  makeSelectFirstList,
  makeSelectHiddenTabs,
  makeSelectSecondList,
  makeSelectPinnedNote,
  makeSelectLoadConfigs,
  makeSelectActiveLists,
  makeSelectOrderDetails,
  makeSelectNotification,
  makeSelectExpandedLists,
  makeSelectLoadDocuments,
  makeSelectMessageCenter,
  makeSelectInvoiceTotals,
  makeSelectBillToVersion,
  makeSelectLinkedLoadList,
  makeSelectActiveListNames,
  makeSelectRouteTelConfigs,
  makeSelectRelatedLoadList,
  makeSelectOrderTotalWeight,
  makeSelectOrderItemsVolume,
  makeSelectMessageCenterList,
  makeSelectLoadCustomStatuses,
  makeSelectOrderTotalDistance,
  makeSelectCustomerInvoiceList,
  makeSelectClaimGeneralDetails,
  makeSelectSelectedCustomerRate,
  makeSelectCubicCapacityWarning,
  makeSelectStatusMessageConfigs,
  makeSelectRelatedLoadListLoading,
  makeSelectCustomerInvoiceNumberList,
  makeSelectOrderTransportationModeGroupingList,
};
